/**
 * Stage 4 — Exploratory Data Analysis (soft-gate).
 *
 * Reads the project's drillhole assays, computes grade summary statistics
 * and a coarse histogram in the browser (small N — the raw values fit
 * comfortably in memory), surfaces outlier count + suggested top-cut, and
 * exposes a manual "Mark reviewed" button that flips Stage 4 to complete.
 *
 * EDA is a *soft* gate — the user can advance without completing it, but
 * doing so raises the amber warning banner defined in StepperShell.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, BarChart3, AlertTriangle } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid,
} from "recharts";
import useAutoFill from "@/hooks/useAutoFill";
import AutoFilledHint from "@/components/AutoFilledHint";
import StageModeSwitcher, { useStageMode } from "@/components/StageModeSwitcher";

const HIST_BINS = 20;

function computeStats(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const mean = sorted.reduce((s, v) => s + v, 0) / n;
  const q = (p) => sorted[Math.min(n - 1, Math.floor(p * n))];
  const median = q(0.5);
  const p95 = q(0.95);
  const p99 = q(0.99);
  const variance = sorted.reduce((s, v) => s + (v - mean) ** 2, 0) / n;
  const std = Math.sqrt(variance);
  const cv = std / (mean || 1);
  // simple outlier rule = above (Q3 + 1.5·IQR), commonly used in EDA
  const q1 = q(0.25), q3 = q(0.75);
  const iqr = q3 - q1;
  const outlierThreshold = q3 + 1.5 * iqr;
  const outliers = sorted.filter((v) => v > outlierThreshold).length;
  // top-cut hint: heavier of p99 or the outlier fence, rounded to 2 sig-figs
  const topCutHint = Math.max(p99, outlierThreshold);
  return {
    n, min: sorted[0], max: sorted[n - 1], mean, median, std, cv,
    p95, p99, q1, q3, iqr, outlierThreshold, outliers, topCutHint,
  };
}

function computeHistogram(values, stats) {
  if (!stats) return [];
  const { min, max } = stats;
  const range = (max - min) || 1;
  const step = range / HIST_BINS;
  const bins = Array.from({ length: HIST_BINS }, (_, i) => ({
    x: min + i * step + step / 2,
    label: (min + i * step).toFixed(2),
    count: 0,
  }));
  for (const v of values) {
    const idx = Math.min(HIST_BINS - 1, Math.floor((v - min) / step));
    bins[idx].count += 1;
  }
  return bins;
}

export default function Stage4EDA({ projectId, onCompleted }) {
  const [_smMode, _setSmMode] = useStageMode(projectId, 4, "ai");
  const [holes, setHoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Auto-fill hook — analytics_date_range + spacing_stats surface here
  // because EDA is the "analytics" surface of the pipeline.
  const auto = useAutoFill(projectId, { sourceStamp: holes.length });
  const dateRangeF = auto.field("analytics_date_range");
  const spacingF = auto.field("spacing_stats");

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/drillholes`).then(({ data }) => {
      setHoles(data || []);
    }).catch(() => setHoles([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  const grades = useMemo(
    () => holes.flatMap((h) => (h.assays || [])
      .map((a) => a.grade)
      .filter((g) => typeof g === "number" && !Number.isNaN(g))),
    [holes],
  );
  const stats = useMemo(() => computeStats(grades), [grades]);
  const hist = useMemo(() => computeHistogram(grades, stats), [grades, stats]);

  const markReviewed = async () => {
    if (!stats) {
      toast.error("No grade data to review yet — import assays first.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/pipeline-state/4/complete`, {
        output_summary: {
          n: stats.n,
          mean: Number(stats.mean.toFixed(3)),
          median: Number(stats.median.toFixed(3)),
          p95: Number(stats.p95.toFixed(3)),
          p99: Number(stats.p99.toFixed(3)),
          outliers: stats.outliers,
          top_cut_hint: Number(stats.topCutHint.toFixed(3)),
        },
      });
      toast.success("Stage 4 (EDA) marked reviewed");
      onCompleted?.();
    } catch (e) {
      toast.error(`Mark reviewed failed: ${e.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="text-[12.5px] text-neutral-500 py-10 text-center" data-testid="stage4-loading">Loading assays…</div>;
  }

  if (!stats) {
    return (
      <div className="border border-dashed border-neutral-300 rounded-lg py-14 text-center bg-neutral-50"
           data-testid="stage4-empty">
        <BarChart3 className="h-7 w-7 text-neutral-500 mx-auto mb-3" />
        <p className="text-[13px] text-neutral-800 font-medium">No assay grades available.</p>
        <p className="text-[11.5px] text-neutral-500 mt-1">
          Complete Stage 2 (Drillhole Import) with assay values to enable EDA.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stage4-content">
      <div className="flex items-center justify-between gap-3 pb-2 border-b border-neutral-100">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-medium">Input mode</div>
        <StageModeSwitcher mode={_smMode} onChange={_setSmMode} testidPrefix="stage4-mode" />
      </div>
      {/* AI-detected default filters (spec: Analytics ≡ EDA in the pipeline) */}
      {(dateRangeF || spacingF) && (
        <div className="border border-primary/30 bg-primary/[0.03] rounded p-3 space-y-2" data-testid="stage4-auto-summary">
          <div className="text-[10px] uppercase tracking-[0.14em] text-primary">AI · default filters detected from your data</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[11.5px] font-mono">
            {dateRangeF && (
              <div data-testid="stage4-auto-date-range">
                <span className="text-neutral-500">Suggested date-range filter</span>
                <div className="tabular-nums">
                  <strong>{dateRangeF.value.from?.slice(0, 10) || "—"}</strong> → <strong>{dateRangeF.value.to?.slice(0, 10) || "—"}</strong>
                </div>
                <AutoFilledHint reason={dateRangeF.reason} source={dateRangeF.source} compact />
              </div>
            )}
            {spacingF && (
              <div data-testid="stage4-auto-spacing">
                <span className="text-neutral-500">Nearest-neighbour collar spacing</span>
                <div className="tabular-nums">
                  median <strong>{spacingF.value.nn_median}m</strong> · range {spacingF.value.nn_min}–{spacingF.value.nn_max}m
                </div>
                <AutoFilledHint reason={spacingF.reason} source={spacingF.source} compact />
              </div>
            )}
          </div>
        </div>
      )}
      {/* KPI ribbon */}
      <div className="grid grid-cols-6 gap-3">
        <KPI label="Assays" value={stats.n} testid="stage4-kpi-n" />
        <KPI label="Mean" value={stats.mean.toFixed(3)} mono testid="stage4-kpi-mean" />
        <KPI label="Median" value={stats.median.toFixed(3)} mono testid="stage4-kpi-median" />
        <KPI label="P95" value={stats.p95.toFixed(3)} mono testid="stage4-kpi-p95" />
        <KPI label="CV" value={stats.cv.toFixed(2)} mono testid="stage4-kpi-cv" />
        <KPI label="Outliers" value={stats.outliers} accent={stats.outliers > 0} testid="stage4-kpi-outliers" />
      </div>

      {/* Histogram */}
      <div className="border border-neutral-200 rounded-md bg-neutral-50 p-4" data-testid="stage4-histogram">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">Grade distribution</div>
            <div className="text-[13px] text-neutral-900 mt-0.5">{HIST_BINS} bins · min {stats.min.toFixed(2)} · max {stats.max.toFixed(2)}</div>
          </div>
          <div className="text-[11px] font-mono tabular-nums text-neutral-500">
            top-cut hint: <span className="text-[#4CAF50]">{stats.topCutHint.toFixed(3)}</span>
          </div>
        </div>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={hist}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6B7280" }} />
              <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} />
              <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E5E7EB", fontSize: 11 }} />
              <ReferenceLine x={hist.reduce((closest, b) =>
                Math.abs(b.x - stats.topCutHint) < Math.abs(closest.x - stats.topCutHint) ? b : closest,
                hist[0]).label} stroke="#4d9c1f" strokeDasharray="4 4"
                label={{ value: "top-cut", fill: "#4d9c1f", fontSize: 10, position: "top" }} />
              <Bar dataKey="count" fill="#4d9c1f" fillOpacity={0.7} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Outlier insight */}
      {stats.outliers > 0 && (
        <div className="border border-amber-500 bg-amber-50 rounded p-3 text-[12px] text-amber-800 flex items-start gap-2"
             data-testid="stage4-outlier-warn">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            {stats.outliers} value(s) above the IQR fence (
            <span className="font-mono">{stats.outlierThreshold.toFixed(3)}</span>).
            Consider top-cutting to <span className="font-mono text-amber-700">{stats.topCutHint.toFixed(3)}</span>
            {" "}in Stage 6 (Compositing) before variography.
          </div>
        </div>
      )}

      {/* Complete action */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <div className="text-[11px] text-neutral-500 italic">
          Soft-gate — advancing without reviewing raises a warning but is permitted.
        </div>
        <button onClick={markReviewed} disabled={saving}
          className="text-[12px] px-4 py-2 bg-[#4CAF50] hover:bg-[#43A047] disabled:bg-neutral-100 disabled:text-neutral-400 rounded text-white font-medium flex items-center gap-1.5 transition"
          data-testid="stage4-mark-reviewed">
          <CheckCircle2 className="h-3.5 w-3.5" /> Mark reviewed
        </button>
      </div>
    </div>
  );
}

function KPI({ label, value, mono, accent, testid }) {
  return (
    <div className={`border rounded-md px-3.5 py-3 ${accent ? "border-amber-500 bg-amber-50" : "border-neutral-200 bg-neutral-50"}`}
         data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-neutral-500">{label}</div>
      <div className={`text-[20px] font-medium mt-1 ${mono ? "font-mono tabular-nums" : ""} ${accent ? "text-amber-700" : ""}`}>{value}</div>
    </div>
  );
}
